import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiRequest } from '@/lib/supabase'

interface Log {
  id: string
  status: string
  comment_text: string | null
  commenter_username: string | null
  matched_keyword: string | null
  trigger_type: string | null
  message_sent: string | null
  error_message: string | null
  rule: { name: string } | null
  created_at: string
}

interface LogsResponse {
  success: boolean
  data: Log[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

const statusColors: Record<string, string> = {
  sent: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  cooldown_skipped: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  no_match: 'bg-gray-100 text-gray-500 dark:bg-white/5 dark:text-gray-400',
}

interface Props {
  isConfigured: boolean
}

export function ExecutionLogs({ isConfigured }: Props) {
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<string>('')

  const { data, isLoading } = useQuery<LogsResponse>({
    queryKey: ['ig-logs', page, statusFilter],
    queryFn: () => apiRequest(`/api/instagram/logs?page=${page}&limit=20${statusFilter ? `&status=${statusFilter}` : ''}`),
    enabled: isConfigured,
  })

  const logs = data?.data || []
  const pagination = data?.pagination

  if (!isConfigured) {
    return (
      <div className="p-5 bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/40 rounded-xl">
        <div className="flex gap-3">
          <span className="material-symbols-outlined text-amber-500 text-xl mt-0.5">settings</span>
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              Instagram integration not set up yet
            </p>
            <p className="text-xs text-amber-600 dark:text-amber-400/70 mt-1 leading-relaxed">
              Execution logs will appear here once the Meta Developer App is configured and an Instagram account is connected.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500 dark:text-gray-400">Filter:</span>
        {['', 'sent', 'failed', 'cooldown_skipped', 'no_match'].map(status => (
          <button
            key={status}
            onClick={() => { setStatusFilter(status); setPage(1) }}
            className={`px-2 py-1 text-[10px] rounded-full border transition-colors ${
              statusFilter === status
                ? 'bg-[#1754cf] text-white border-[#1754cf]'
                : 'border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5'
            }`}
          >
            {status || 'All'}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-14 bg-gray-100 dark:bg-white/5 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-8 text-sm text-gray-400">
          No execution logs yet
        </div>
      ) : (
        <div className="space-y-1.5">
          {logs.map(log => (
            <div
              key={log.id}
              className="flex items-start gap-3 p-3 bg-white dark:bg-[#1c2230] border border-gray-200 dark:border-white/10 rounded-lg"
            >
              <span className={`shrink-0 mt-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded-full ${statusColors[log.status] || ''}`}>
                {log.status.replace('_', ' ')}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-xs">
                  {log.commenter_username && (
                    <span className="font-medium text-gray-700 dark:text-gray-300">@{log.commenter_username}</span>
                  )}
                  {log.rule && (
                    <span className="text-gray-400">Rule: {log.rule.name}</span>
                  )}
                </div>
                {log.comment_text && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                    Comment: "{log.comment_text}"
                  </p>
                )}
                {log.error_message && (
                  <p className="text-xs text-red-500 mt-0.5 truncate">Error: {log.error_message}</p>
                )}
              </div>
              <span className="text-[10px] text-gray-400 shrink-0">
                {new Date(log.created_at).toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-30"
          >
            Previous
          </button>
          <span className="text-xs text-gray-400">
            Page {pagination.page} of {pagination.pages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
            disabled={page >= pagination.pages}
            className="px-3 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-30"
          >
            Next
          </button>
        </div>
      )}
    </div>
  )
}
